import { Component, OnInit } from '@angular/core';
import { SharedModule } from '../../shared/shared.module';

import {
  NavigationItem,
  navigationItemsNoPatient,
} from '../../models/navigation-item-model';
import { DoctorService } from '../../services/doctor.service';
import { DoctorPersonalArea } from '../../models/doctor-model';
import { LabelService } from '../../services/label.service';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ErrorPanelService } from '../../services/error-panel.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  CustomerPortalResponse,
  ModifySubscriptionAtPeriodEndResponse,
  SubscriptionData,
  ClineaseSubscriptionStatus,
} from '../../models/payments-model';
import { PaymentsService } from '../../services/payments.service';
import { Usage } from '../../models/api/responses/personal-area.response';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationPanelComponent } from '../../shared/confirmation-panel/confirmation-panel.component';
@Component({
  selector: 'app-personal-area-page',
  standalone: true,
  imports: [
    SharedModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './personal-area-page.component.html',
  styleUrl: './personal-area-page.component.scss',
})
export class PersonalAreaPageComponent implements OnInit {
  navigationItems: NavigationItem[] = navigationItemsNoPatient;
  profilePicture: string = '';
  doctorForm: FormGroup;
  isEditing: boolean = false;
  isFiscalCodeManuallyEdited: boolean = false;
  doctorRole: string = '';
  showConfirmationPanelForDeleteAccount: boolean = false;
  isDeletingAccount: boolean = false;

  subscriptionData: SubscriptionData | null = null;
  daysUntilSubscriptionRenewal: number = -1;
  usage: Usage | null = null;
  usageRequestFailed = false;

  get isAdmin(): boolean {
    return this.doctorRole === 'admin';
  }

  get isSubscriptionActive(): boolean {
    return this.subscriptionData?.status === ClineaseSubscriptionStatus.ACTIVE;
  }

  get subscriptionStatus(): string {
    let statusToTranslate: string;
    if (this.subscriptionData?.status) {
      statusToTranslate =
        this.subscriptionData.status === ClineaseSubscriptionStatus.ACTIVE
          ? 'Attivo'
          : 'Non Attivo';
    } else {
      statusToTranslate = 'Non Attivo';
    }
    return this.labelService.getLabel(statusToTranslate);
  }

  get cancelingAtPeriodEnd(): boolean {
    return !!this.subscriptionData?.cancel_at_period_end;
  }

  get shouldShowSubscriptionDateCard(): boolean {
    return this.isAdmin || this.isSubscriptionActive;
  }

  get subscriptionDateTitle(): string {
    if (this.isAdmin) {
      return this.labelService.getLabel('Data Prossimo Rinnovo');
    }
    if (this.isSubscriptionActive && this.cancelingAtPeriodEnd) {
      return this.labelService.getLabel('Data Fine Abbonamento');
    }
    return this.labelService.getLabel('Data Prossimo Rinnovo');
  }

  get subscriptionDateValue(): string {
    if (this.isAdmin) {
      return 'N/A';
    }
    return this.subscriptionData?.current_period_end || 'N/A';
  }

  constructor(
    private fb: FormBuilder,
    private doctorService: DoctorService,
    public labelService: LabelService,
    public errorPanelService: ErrorPanelService,
    private paymentsService: PaymentsService,
    public dialog: MatDialog,
    private router: Router
  ) {
    this.doctorForm = this.fb.group({
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      signin_mail: ['', [Validators.email]],
    });
    this.doctorForm.disable();
  }

  ngOnInit(): void {
    this.getDoctorFields();
    this.profilePicture = sessionStorage.getItem('profilePicture') || '';

    this.doctorRole = sessionStorage.getItem('role') || '';
    if (this.doctorRole !== 'admin') {
      this.getSubscriptionData();
    }
    this.getUsage();
  }

  getDoctorFields() {
    this.doctorService.getDoctorPersonalArea().subscribe({
      next: (doctor: DoctorPersonalArea) => {
        this.doctorForm.patchValue(doctor);
      },
      error: (error) => {
        console.error('Error:', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore nel recuperare i dati'
        );
      },
    });
  }

  onEditClick(): void {
    this.isEditing = true;
    this.doctorForm.enable();
    this.doctorForm.get('signin_mail')?.disable();
  }

  onSaveClick(): void {
    let updatedDoctor: DoctorPersonalArea = { ...this.doctorForm.value };
    this.doctorForm.disable();

    this.doctorService.updateDoctorPersonalArea(updatedDoctor).subscribe({
      next: () => {
        this.isEditing = false;
        this.getDoctorFields();
      },
      error: (error) => {
        console.error('Error:', error);
        this.errorPanelService.showErrorSnackBar(
          "Errore nell'aggiornamento dei dati"
        );
      },
    });
  }

  onCancelClick(): void {
    this.isEditing = false;
    this.getDoctorFields();
    this.doctorForm.disable();
  }

  getSubscriptionData() {
    this.paymentsService.getSubscriptionData().subscribe({
      next: (subscriptionData: SubscriptionData) => {
        this.subscriptionData = subscriptionData;
        if (this.subscriptionData?.current_period_end) {
          const subscriptionEndDate = this.parseDdMmYyyy(
            this.subscriptionData.current_period_end
          );
          if (subscriptionEndDate) {
            const currentDate = new Date();
            const timeDifference =
              subscriptionEndDate.getTime() - currentDate.getTime();
            this.daysUntilSubscriptionRenewal = Math.ceil(
              timeDifference / (1000 * 60 * 60 * 24)
            );
          }
        }
      },
      error: (error) => {
        console.error('Error:', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore nel recuperare i dati'
        );
      },
    });
  }

  openStripeClientPortal() {
    this.paymentsService.createCustomerPortalSession().subscribe({
      next: (response: CustomerPortalResponse) => {
        window.open(response.portal_url, '_blank');
      },
    });
  }

  toggleSubscriptionAtPeriodEnd() {
    const nextValue = !this.cancelingAtPeriodEnd;
    if (nextValue) {
      const dialogRef = this.dialog.open(ConfirmationPanelComponent, {
        data: {
          title: "Sei sicuro di volere disdirre l'abbonamento?",
          infoText:
            'Se prosegui non potrai più usufruire di eventuali sconti attivi sul tuo abbonamento',
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.updateSubscriptionAtPeriodEnd(nextValue);
        }
      });
      return;
    }

    this.updateSubscriptionAtPeriodEnd(nextValue);
  }

  private updateSubscriptionAtPeriodEnd(nextValue: boolean) {
    this.paymentsService.modifySubscription(nextValue).subscribe({
      next: (response: ModifySubscriptionAtPeriodEndResponse) => {
        if (this.subscriptionData) {
          this.subscriptionData.cancel_at_period_end =
            response.cancel_at_period_end;
        }
        this.errorPanelService.showInfoSnackBar(
          'Abbonamento aggiornato con successo'
        );
        this.getSubscriptionData();
      },
      error: (error) => {
        console.error('Error:', error);
        this.errorPanelService.showErrorSnackBar(
          "Errore nell'aggiornamento dell'abbonamento"
        );
      },
    });
  }

  getUsage() {
    this.usage = null;
    this.usageRequestFailed = false;
    this.doctorService.getUsage().subscribe({
      next: (usage: Usage) => {
        this.usage = usage;
      },
      error: (error) => {
        console.error('Error:', error);
        this.usageRequestFailed = true;
      },
    });
  }

  onDeleteClick(): void {
    const dialogRef = this.dialog.open(ConfirmationPanelComponent, {
      data: {
        title: 'Sei sicuro di voler eliminare il tuo account?',
        infoText:
          'Verranno immediatamente cancellati tutti i tuoi dati e quelli relativi ai tuoi pazienti, inclusi tutti i documenti e risultati di pipeline AI. Questa azione è irreversibile. Eliminando il tuo account non potrai più richiedere una copia dei tuoi dati.',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteAccount();
      }
    });
  }

  deleteAccount() {
    this.isDeletingAccount = true;
    this.doctorService.deleteDoctor().subscribe({
      next: () => {
        this.errorPanelService.showInfoSnackBar(
          'Il tuo account, e tutti i dati associati, sono stati eliminati con successo'
        );
        this.isDeletingAccount = false;
        this.router.navigate(['/']);
        sessionStorage.clear();
      },
      error: (error) => {
        console.error("Errore durante l'eliminazione del profilo:", error);
        this.errorPanelService.showErrorSnackBar(
          "Errore durante l'eliminazione del profilo"
        );
        this.isDeletingAccount = false;
      },
    });
  }

  // TODO: move this to a utils service together with the calendar utils service. Move this service in a common space.
  private parseDdMmYyyy(value: string): Date | null {
    const parts = value.split('/');
    if (parts.length !== 3) {
      return null;
    }
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    if ([day, month, year].some((n) => Number.isNaN(n))) {
      return null;
    }
    return new Date(year, month - 1, day);
  }
}
